//
//
//
//

import { mapGetters, mapActions } from 'vuex'

const POLL_INTERVAL_MS = 5000

// Global, hidden watcher (mirrors ReportStatusWatcher). Polls the user's RFI import
// jobs while any are in progress and fires a notification when one is ready to review,
// so the user isn't blocked. Clicking the notification opens the dedicated review page.
// Polling pauses while the tab is hidden (every open tab mounts a watcher) and catches up
// with one fetch when it becomes visible again; a transition that happened meanwhile is
// still noticed, because it is detected by comparing against the last statuses seen.
export default {
  name: 'RfiImportStatusWatcher',
  data() {
    return {
      pollTimer: null,
      jobStatusById: {}
    }
  },
  computed: {
    ...mapGetters({
      importJobs: 'rfiImport/getImportJobs',
      hasPendingImportJobs: 'rfiImport/hasPendingImportJobs',
      userId: 'user/userId',
      // Same test the server's onlyProjectOwners applies (acting-as project owner,
      // which includes a reviewer acting as one). Anyone else would only poll a 403.
      canImport: 'user/isProjectOwner'
    })
  },
  watch: {
    hasPendingImportJobs(pending) {
      if (pending) { this.startPolling() } else { this.stopPolling() }
    },
    importJobs(jobs) {
      this.notifyOnTransitions(jobs || [])
    },
    // Switching role (participant <-> project owner) doesn't remount the layout.
    canImport(can) {
      if (can) { this.start() } else { this.stopPolling() }
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.start()
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.stopPolling()
  },
  methods: {
    ...mapActions({
      fetchImportJobs: 'rfiImport/fetchImportJobs'
    }),
    async start() {
      if (!this.canImport) { return }
      try {
        await this.fetchImportJobs()
      } catch (err) {
        // A failed initial fetch (deploy, expired session) must not surface as an
        // unhandled rejection from every layout — the next poll simply retries.
        return
      }
      this.jobStatusById = (this.importJobs || []).reduce((acc, j) => {
        acc[j._id] = j.status
        return acc
      }, {})
      if (this.hasPendingImportJobs) {
        this.startPolling()
      }
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.stopPolling()
        return
      }
      if (!this.canImport) { return }
      this.fetchImportJobs()
        .then(() => { if (this.hasPendingImportJobs) { this.startPolling() } })
        .catch(() => {})
    },
    startPolling() {
      if (this.pollTimer || !this.canImport || document.hidden) { return }
      this.pollTimer = setInterval(() => {
        // Swallow transient poll failures — the next tick retries.
        this.fetchImportJobs().catch(() => {})
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    review(job, toastId) {
      if (toastId) { this.$bvToast.hide(toastId) }
      this.$router.push(`/templates/imports/${job._id}/review`)
    },
    // The list is org-wide (every project owner sees every import on the dashboard),
    // but a toast on every page is for the person who started the job; a colleague's
    // import finishing is not an interruption.
    isMine(job) {
      const owner = job.owner && typeof job.owner === 'object' ? job.owner._id : job.owner
      return !!owner && String(owner) === String(this.userId || '')
    },
    // The reason is kept (some failures, e.g. an encrypted PDF, won't go away on a retry),
    // but while the original is still preserved the user is told a retry is one click on
    // the imports page rather than left to think a re-upload is the only way.
    failureMessage(job) {
      const reason = job.error || 'The RFI import failed.'
      if (!job.canRetry || /\bretry\b/i.test(reason)) { return reason }
      return `${reason} You can retry it from the RFI Imports page.`
    },
    notifyOnTransitions(jobs) {
      const next = {}
      const pending = ['extracting', 'converting']
      for (const job of jobs) {
        next[job._id] = job.status
        const prev = this.jobStatusById[job._id]
        if (!this.isMine(job)) { continue }
        // Already looking at it: the review page loads the draft by itself.
        const onItsReviewPage = this.$route && this.$route.path === `/templates/imports/${job._id}/review`
        if (pending.includes(prev) && job.status === 'ready_preview' && !onItsReviewPage) {
          const toastId = `rfi-import-${job._id}`
          const h = this.$createElement
          const body = h('div', [
            h('div', { class: 'mb-2' }, `"${job.sourceFilename || 'Your RFI'}" is ready to review.`),
            h('a', {
              attrs: { href: '#' },
              class: 'alert-link',
              on: {
                click: (e) => {
                  e.preventDefault()
                  this.review(job, toastId)
                }
              }
            }, 'Review & create template →')
          ])
          this.$bvToast.toast(body, {
            id: toastId,
            title: 'RFI import ready',
            variant: 'success',
            toaster: 'b-toaster-top-right',
            solid: true,
            noAutoHide: true
          })
        } else if (pending.includes(prev) && job.status === 'failed') {
          this.$bvToast.toast(this.failureMessage(job), {
            title: 'RFI import failed',
            variant: 'danger',
            toaster: 'b-toaster-top-right',
            solid: true
          })
        }
      }
      this.jobStatusById = next
    }
  }
}
